import { createContext, useContext, useEffect, useMemo, useRef, type Dispatch, type SetStateAction } from 'react'
import { haptic, tg } from '../lib/telegram'

export interface MainButtonConfig {
  label: string
  onClick: () => void
  disabled?: boolean
  loading?: boolean
}

type Setter = Dispatch<SetStateAction<MainButtonConfig | null>>

/** Состояние живёт в App; экраны только записывают в него через `useMainButton`. */
export const MainButtonContext = createContext<Setter | null>(null)

/**
 * Экран объявляет главную кнопку приложения. В Telegram она одна на всё
 * приложение (WebApp.MainButton), поэтому конфигурация — глобальная,
 * а рисует её единственный `MainButtonSlot` внизу экрана.
 */
export function useMainButton(config: MainButtonConfig | null): void {
  const setConfig = useContext(MainButtonContext)
  const label = config?.label
  const disabled = config?.disabled
  const loading = config?.loading

  // onClick — новая функция на каждый рендер; держим её в ref, чтобы эффект
  // не перезапускался из-за смены идентичности колбэка.
  const handler = useRef(config?.onClick)
  handler.current = config?.onClick

  const stable = useMemo<MainButtonConfig | null>(
    () => (label ? { label, disabled, loading, onClick: () => handler.current?.() } : null),
    [label, disabled, loading],
  )

  useEffect(() => {
    setConfig?.(stable)
    return () => setConfig?.((current) => (current === stable ? null : current))
  }, [setConfig, stable])
}

/** Рисует нативную MainButton в Telegram либо золотую кнопку из макета в браузере. */
export function MainButtonSlot({ config }: { config: MainButtonConfig | null }) {
  const handler = useRef(config?.onClick)
  handler.current = config?.onClick

  /*
   * Родную кнопку Telegram не используем.
   *
   * Она выглядит нативно, но Telegram всегда рисует её в самом низу экрана —
   * под нашей навигацией и со своим фоном, который не совпадает с фоном
   * приложения. Ни положение, ни цвет изменить нельзя. Раз действие должно
   * лежать над навигацией и в общей палитре, рисуем свою кнопку, а родную
   * держим скрытой, чтобы не появлялась вторая.
   */
  useEffect(() => {
    if (!tg) return
    try {
      tg.MainButton.hide()
    } catch {
      /* старые клиенты — не критично */
    }
  }, [config])

  if (!config) return null

  return (
    <div className="mainbutton">
      <button
        type="button"
        disabled={config.disabled || config.loading}
        onClick={() => {
          haptic.impact('medium')
          config.onClick()
        }}
      >
        {config.loading ? '…' : config.label}
      </button>
    </div>
  )
}
